import React, { useState } from 'react';
import { changePassword } from '../../../api/user';
import { useAuthStore } from '../../../stores/authStore';
import { handleApiError, showErrorNotification } from '../../../utils/errorHandler';

export default function SecurityForm() {
  const { signOut } = useAuthStore();
  const [form, setForm] = useState({ currentPwd: '', newPwd: '', confirmPwd: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const set = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const strength = (() => {
    const p = form.newPwd;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const strengthLabel = ['', '弱', '中', '强', '非常强'][strength];
  const strengthColor = ['', 'bg-red-400', 'bg-yellow-400', 'bg-teal-400', 'bg-teal-500'][strength];
  const strengthText = ['', 'text-red-500', 'text-yellow-500', 'text-teal-600', 'text-teal-600'][strength];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('idle');
    if (!form.currentPwd) { setErrMsg('请输入当前密码'); setStatus('err'); return; }
    if (form.newPwd !== form.confirmPwd) { setErrMsg('两次密码不一致'); setStatus('err'); return; }
    if (form.newPwd.length < 8) { setErrMsg('密码至少 8 位'); setStatus('err'); return; }
    setSaving(true);
    try {
      await changePassword({ currentPassword: form.currentPwd, newPassword: form.newPwd });
      setStatus('ok');
      setForm({ currentPwd: '', newPwd: '', confirmPwd: '' });
      // 改密成功后，后端已通过 last_password_change 失效所有旧 token；
      // 前端主动清理会话并跳登录，给用户几秒看提示。
      setTimeout(async () => {
        await signOut();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }, 1500);
    } catch (error) {
      const errorInfo = handleApiError(error);
      setErrMsg(errorInfo.message);
      setStatus('err');
      showErrorNotification(errorInfo);
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 pr-10 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="w-full sm:max-w-md space-y-5">
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-lg mb-2">
        <i className="ri-information-line text-amber-500 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">密码安全提示</p>
          <p className="text-xs text-amber-600 mt-0.5">建议使用 8 位以上含大小写字母、数字及特殊字符的密码，并定期更换。</p>
        </div>
      </div>

      {/* Current Password */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">当前密码</label>
        <div className="relative">
          <input type={show.current ? 'text' : 'password'} name="currentPwd" value={form.currentPwd} onChange={set} placeholder="请输入当前密码" className={inp} required />
          <button type="button" onClick={() => setShow(s => ({ ...s, current: !s.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-4 h-4 flex items-center justify-center">
            <i className={show.current ? 'ri-eye-off-line' : 'ri-eye-line'} />
          </button>
        </div>
      </div>

      {/* New Password */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">新密码</label>
        <div className="relative">
          <input type={show.new ? 'text' : 'password'} name="newPwd" value={form.newPwd} onChange={set} placeholder="请输入新密码" className={inp} required />
          <button type="button" onClick={() => setShow(s => ({ ...s, new: !s.new }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-4 h-4 flex items-center justify-center">
            <i className={show.new ? 'ri-eye-off-line' : 'ri-eye-line'} />
          </button>
        </div>
        {form.newPwd && (
          <div className="mt-2 space-y-1">
            <div className="flex gap-1">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength ? strengthColor : 'bg-gray-200'}`} />
              ))}
            </div>
            <p className="text-xs text-gray-500">强度：<span className={`font-medium ${strengthText}`}>{strengthLabel}</span></p>
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">确认新密码</label>
        <div className="relative">
          <input type={show.confirm ? 'text' : 'password'} name="confirmPwd" value={form.confirmPwd} onChange={set} placeholder="再次输入新密码" className={inp} required />
          <button type="button" onClick={() => setShow(s => ({ ...s, confirm: !s.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-4 h-4 flex items-center justify-center">
            <i className={show.confirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
          </button>
        </div>
        {form.confirmPwd && (
          <p className={`text-xs mt-1 flex items-center gap-1 ${form.newPwd === form.confirmPwd ? 'text-teal-600' : 'text-red-500'}`}>
            <i className={form.newPwd === form.confirmPwd ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} />
            {form.newPwd === form.confirmPwd ? '密码一致' : '密码不一致'}
          </p>
        )}
      </div>

      {status === 'err' && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
          <i className="ri-error-warning-line" />{errMsg}
        </div>
      )}
      {status === 'ok' && (
        <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-sm text-teal-600">
          <i className="ri-checkbox-circle-line" />密码已成功修改，即将跳转登录页请重新登录…
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap cursor-pointer"
      >
        {saving ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />修改中...</span> : '修改密码'}
      </button>
    </form>
  );
}
